import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { MultiSelectChangeEvent } from 'primeng/multiselect';
import { Observable, Subject, takeUntil } from 'rxjs';
import { FilterService } from 'src/app/services/filter.service';
import { GeographicService } from 'src/app/services/geographic.service';
import { GeographicFilterRegionModel } from '../../../models/geographic-filter-region-model';


@Component({
  selector: 'ness-geographic-filter-region',
  templateUrl: './geographic-filter-region.component.html'
})
export class GeographicFilterRegionComponent implements OnInit, OnDestroy {
  @Input() geoFilterStorageKey: string = null;
  @Input() geoFilterVerbiage: string = 'Filter by:';
  @Input() suppressUI: boolean = false;
  geographicFilterRegionModel: GeographicFilterRegionModel = new GeographicFilterRegionModel('all', 'all', 'all', [], 'All', 'All', 'All', 'All', 'All', 'All', 'All', [], 'All', 'All', 'All', []);
  fieldDivisions$: Observable<ServerModels.ENESS_REF_FieldDivisions[]> = this.geographicService.fieldDivisions;
  states$: Observable<ServerModels.ENESS_REF_State[]> = this.geographicService.states;
  counties$: Observable<ServerModels.ENESS_REF_Counties[]> = this.geographicService.counties;
  fdgroups$: Observable<ServerModels.FieldDivisionGroupViewModel[]> = this.geographicService.fdgroups;
  agencies$: Observable<ServerModels.AgencyViewModel[]> = this.geographicService.agencies;
  public inEditMode: boolean = false;

  private _destroyed$: Subject<void> = new Subject();

  constructor(private geographicService: GeographicService,
    public filterService: FilterService,
    public elementRef: ElementRef) { }

  ngOnInit(): void {
    this.applyFilters();
    this.geographicService.geographicFilterModel.pipe(takeUntil(this._destroyed$)).subscribe((fm: GeographicFilterRegionModel) => {
      if (!!fm && Object.keys(fm).length > 0) {
        this.geographicFilterRegionModel = fm;
        this.SetInEditMode();
      }
    });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  public applyFilters(): void {
    this.geographicService.applyGeoFilters(this.geoFilterStorageKey);
  }

  public getSelectedFilterValues(): GeographicFilterRegionModel {
    this.agencies$.pipe(takeUntil(this._destroyed$)).subscribe((agencies: ServerModels.AgencyViewModel[]) => {
      this.geographicFilterRegionModel.agenciesTotalCount = agencies?.length;
    });
    this.counties$.pipe(takeUntil(this._destroyed$)).subscribe((counties: ServerModels.ENESS_REF_Counties[]) => {
      this.geographicFilterRegionModel.countiesTotalCount = counties?.length;
    });
    this.fdgroups$.pipe(takeUntil(this._destroyed$)).subscribe((fdgroups: ServerModels.FieldDivisionGroupViewModel[]) => {
      this.geographicFilterRegionModel.fdgroupsTotalCount = fdgroups?.length;
    });

    let selectedValues = this.geographicFilterRegionModel;

    return this.setNameListValue(selectedValues);
  }

  public resetToDefaultFilters(): void {
    this.geographicService.resetGeoFilterDefaults(this.geoFilterStorageKey);
  }

  public onSelectFieldDivision(target: EventTarget): void {
    const fieldDivision: HTMLSelectElement = <HTMLSelectElement>target;
    this.geographicFilterRegionModel = new GeographicFilterRegionModel(fieldDivision.value, 'all', 'all', [], fieldDivision.options[fieldDivision.selectedIndex].text, 'All', 'All', 'All', 'All', 'All', 'All', [], 'All', 'All', 'All', []);
    this.geographicService.getFDGroups(fieldDivision.value);
    this.geographicService.getStates(fieldDivision.value, 'all');
    this.geographicService.getCounties(fieldDivision.value, 'all', 'all')
    this.geographicService.getAgencies(fieldDivision.value, this.geographicFilterRegionModel.fdgroupNameList, this.geographicFilterRegionModel.stateID, this.geographicFilterRegionModel.countyNameList);
    this.SetInEditMode();
  }

  public onSelectFDGroup(evt: MultiSelectChangeEvent): void {
    this.fdgroups$.pipe(takeUntil(this._destroyed$)).subscribe((groups: ServerModels.FieldDivisionGroupViewModel[]) => {
      if (evt.value?.length == 0 && !evt.itemValue) {
        this.geographicFilterRegionModel.fdgroupNameList = 'all';
      } else if (evt.value?.length != groups?.length) {
        this.geographicFilterRegionModel.fdgroupNameList = '';
        evt.value.forEach(group => this.geographicFilterRegionModel.fdgroupNameList += `${group.IntelligenceReportATFReferralGroupsID}; `);
      } else  {
        this.geographicFilterRegionModel.fdgroupNameList = 'all';
      }

      this.geographicFilterRegionModel = new GeographicFilterRegionModel(this.geographicFilterRegionModel.fieldDivisionID, 'all', 'all', [], this.geographicFilterRegionModel.fieldDivisionName, 'All', 'All', 'All', 'All', 'All', 'All', [], 'All', 'All', this.geographicFilterRegionModel.fdgroupNameList, this.geographicFilterRegionModel.fdgroups);
      this.geographicService.getStates(this.geographicFilterRegionModel.fieldDivisionID, this.geographicFilterRegionModel.fdgroupNameList);
      this.geographicService.getCounties(this.geographicFilterRegionModel.fieldDivisionID, this.geographicFilterRegionModel.fdgroupNameList, 'all')
      this.geographicService.getAgencies(this.geographicFilterRegionModel.fieldDivisionID, this.geographicFilterRegionModel.fdgroupNameList, 'all', 'all');
      this.SetInEditMode();
    });
  }

  public onSelectState(target: EventTarget): void {
    const state: HTMLSelectElement = <HTMLSelectElement>target;
    this.geographicFilterRegionModel.stateID = state.value;
    this.geographicFilterRegionModel.stateName = state.options[state.selectedIndex].text;
    this.geographicService.getCounties(this.geographicFilterRegionModel.fieldDivisionID, this.geographicFilterRegionModel.fdgroupNameList, state.value);
    this.geographicService.getAgencies(this.geographicFilterRegionModel.fieldDivisionID, this.geographicFilterRegionModel.fdgroupNameList, state.value, 'All');
    this.SetInEditMode();
  }

  public onSelectCounties(evt: MultiSelectChangeEvent): void {
    this.counties$.pipe(takeUntil(this._destroyed$)).subscribe((counties: ServerModels.ENESS_REF_Counties[]) => {
      if (evt.value?.length == 0 || !evt.itemValue) {
        this.geographicFilterRegionModel.countyNameList = 'all';
      } else if (evt.value?.length != counties?.length) {
        this.geographicFilterRegionModel.countyNameList = '';
        evt.value.forEach(county => this.geographicFilterRegionModel.countyNameList += `${county.CountyID}; `);
      } else {
        this.geographicFilterRegionModel.countyNameList = 'all';
      }

      this.geographicFilterRegionModel = new GeographicFilterRegionModel(this.geographicFilterRegionModel.fieldDivisionID, this.geographicFilterRegionModel.stateID, 'all', this.geographicFilterRegionModel.counties, this.geographicFilterRegionModel.fieldDivisionName, this.geographicFilterRegionModel.stateName, 'All', this.geographicFilterRegionModel.countyNameList, 'All', 'All', 'All', [], 'All', 'All', this.geographicFilterRegionModel.fdgroupNameList, this.geographicFilterRegionModel.fdgroups);
      this.geographicService.getAgencies(this.geographicFilterRegionModel.fieldDivisionID, this.geographicFilterRegionModel.fdgroupNameList, this.geographicFilterRegionModel.stateID, this.geographicFilterRegionModel.countyNameList);
    });

  }

  public onSelectAgencies(evt: MultiSelectChangeEvent): void {
    this.agencies$.pipe(takeUntil(this._destroyed$)).subscribe((agencies: ServerModels.AgencyViewModel[]) => {
      if (evt.value?.length == 0 || !evt.itemValue) {
        this.geographicFilterRegionModel.agenciesNameList = 'all';
      } else if (evt.value?.length != agencies?.length) {
        this.geographicFilterRegionModel.agenciesNameList = '';
        evt.value.forEach(agency => this.geographicFilterRegionModel.agenciesNameList += `${agency.AgencyID}; `);
      } else {
        this.geographicFilterRegionModel.agenciesNameList = 'all';
      }
    });

  }

  public SetInEditMode(): void {
    if (this.geographicFilterRegionModel.fieldDivisionID.toLowerCase() !== 'all' ||
      (this.geographicFilterRegionModel.fdgroupNameList !== '' && this.geographicFilterRegionModel.fdgroupNameList.toLowerCase() !== 'all') ||
      this.geographicFilterRegionModel.stateID.toLowerCase() !== 'all'
    ) {
      this.inEditMode = true;
    }
    else {
      this.inEditMode = false;
    }
  }

  private setNameListValue(filterValue: GeographicFilterRegionModel) {
    if (filterValue?.fdgroups.length > 0 && filterValue?.fdgroupNameList.toLowerCase() !== 'all') {
      let fdGroupNames: string[] = [];
      fdGroupNames = filterValue.fdgroups.map(group => `${group.GroupName}`);
      filterValue.fdgroupName = fdGroupNames.join('; ');
    } else {
      filterValue.fdgroupName = 'All';
    }

    if (filterValue?.counties.length > 0 && filterValue?.countyNameList.toLowerCase() !== 'all') {
      let counties: string[] = filterValue.counties.map(c => `${c.CountyName}, ${c.ENESS_REF_State.StateAbbreviation}`);
      filterValue.countyName = counties.join('; ');
    } else {
      filterValue.countyName = 'All';
    }

    if (filterValue?.agencies.length > 0 && filterValue?.agenciesNameList.toLowerCase() !== 'all') {
      let agencies: string[] = [];
      agencies = filterValue.agencies.map(a => `${a.AgencyName}`);
      filterValue.agencyName = agencies.join('; ');
    } else {
      filterValue.agencyName = 'All';
    }

    return filterValue;
  }
}
